import React, { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MainDrawer } from '../Admin/CommonComponent/MainDrawer';
import { OKRButton } from '../Common/OKRButton';
import { BorderCheckboxIcon, CheckedIcon } from '../../config/svg/formElementIcons';
import { UploadIcon } from '../../config/svg/Action';
import { CrossIcon } from '../../config/svg/CommonSvgIcon';
import { VersionOption, WhatsNewDocumentRequest, WhatsNewEntity, WhatsNewTypeOption } from './mockData';
import {
	createWhatsNewAPI,
	deleteWhatsNewScreenshotAPI,
	updateWhatsNewAPI,
	uploadWhatsNewScreenshotAPI,
} from '../../services/whatsNewRoadmapService';

const ACCEPTED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const MAX_SCREENSHOT_SIZE_KB = 5120;

const getTodayDateString = () => {
	const now = new Date();
	const offset = now.getTimezoneOffset();
	return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
};

interface AddUpdateDrawerProps {
	open: boolean;
	handleClose: () => void;
	onSaved: () => void;
	whatsNewTypes: WhatsNewTypeOption[];
	versions: VersionOption[];
	editEntity?: WhatsNewEntity | null;
}

const emptyFormState = (defaultTypeId?: number, defaultVersionId?: number) => ({
	typeId: defaultTypeId,
	title: '',
	description: '',
	version: defaultVersionId,
	releaseDate: '',
	admins: false,
	users: true,
	status: 'draft' as 'draft' | 'published',
});

const formStateFromEntity = (entity: WhatsNewEntity) => ({
	typeId: entity.whatsNewType,
	title: entity.title,
	description: entity.description,
	version: entity.versionId,
	releaseDate: entity.releaseDate ? entity.releaseDate.slice(0, 10) : '',
	admins: entity.isAdminImpacted,
	users: entity.isUserImpacted,
	status: (entity.isPublished ? 'published' : 'draft') as 'draft' | 'published',
});

export const AddUpdateDrawer: React.FC<AddUpdateDrawerProps> = ({
	open,
	handleClose,
	onSaved,
	whatsNewTypes,
	versions,
	editEntity,
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [formData, setFormData] = useState(
		emptyFormState(whatsNewTypes[0]?.whatsNewTypeId, versions[0]?.oneGuruVersionId)
	);
	const [saving, setSaving] = useState(false);
	const [screenshots, setScreenshots] = useState<WhatsNewDocumentRequest[]>([]);
	const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
	const [deletingScreenshotIndex, setDeletingScreenshotIndex] = useState<number | null>(null);
	const [isDragActive, setIsDragActive] = useState(false);
	const screenshotInputRef = useRef<HTMLInputElement>(null);
	const todayDateString = getTodayDateString();

	useEffect(() => {
		if (!open) {
			return;
		}
		setFormData(
			editEntity
				? formStateFromEntity(editEntity)
				: emptyFormState(whatsNewTypes[0]?.whatsNewTypeId, versions[0]?.oneGuruVersionId)
		);
		setScreenshots(editEntity?.documentList || editEntity?.documentRequests || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editEntity]);

	const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCancel = () => {
		setFormData(emptyFormState(whatsNewTypes[0]?.whatsNewTypeId, versions[0]?.oneGuruVersionId));
		setScreenshots([]);
		handleClose();
	};

	const uploadScreenshotFiles = async (fileList: FileList | null) => {
		if (!fileList || !fileList.length) {
			return;
		}
		setUploadingScreenshot(true);
		for (const file of Array.from(fileList)) {
			const sizeKb = Math.round(file.size / 1024);
			if (!ACCEPTED_SCREENSHOT_TYPES.includes(file.type.toLowerCase())) {
				enqueueSnackbar(t('fileType4FeatureUpload2'), { variant: 'error', autoHideDuration: 5000 });
				continue;
			}
			if (sizeKb > MAX_SCREENSHOT_SIZE_KB) {
				enqueueSnackbar(t('fileSize5MB'), { variant: 'error', autoHideDuration: 5000 });
				continue;
			}
			const form = new FormData();
			form.append('UploadFiles', file);
			try {
				const response: any = await uploadWhatsNewScreenshotAPI(form);
				const filePath = response?.data?.entity;
				if (filePath) {
					setScreenshots((prev) => [
						...prev,
						{
							documentId: 0,
							documentModuleId: 2,
							moduleDetailsId: 0,
							uploadFileName: file.name,
							filePath,
							storageFileName: filePath.substring(filePath.lastIndexOf('/') + 1),
						},
					]);
				} else {
					enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
				}
			} catch (error) {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		}
		setUploadingScreenshot(false);
	};

	const handleScreenshotInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		uploadScreenshotFiles(event.target.files);
		event.target.value = '';
	};

	const handleScreenshotDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragActive(false);
		uploadScreenshotFiles(event.dataTransfer.files);
	};

	const handleScreenshotDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragActive(true);
	};

	const handleScreenshotDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragActive(false);
	};

	const removeScreenshot = async (index: number) => {
		const doc = screenshots[index];
		if (!doc?.documentId) {
			setScreenshots((prev) => prev.filter((_, i) => i !== index));
			return;
		}
		setDeletingScreenshotIndex(index);
		try {
			const response: any = await deleteWhatsNewScreenshotAPI(doc.documentId, doc.documentModuleId);
			if (response?.data?.isSuccess) {
				setScreenshots((prev) => prev.filter((_, i) => i !== index));
			} else {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		} catch (error) {
			enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
		} finally {
			setDeletingScreenshotIndex(null);
		}
	};

	const isFormValid =
		Boolean(formData.title.trim()) &&
		Boolean(formData.description.trim()) &&
		formData.typeId !== undefined &&
		formData.version !== undefined &&
		Boolean(formData.releaseDate) &&
		formData.releaseDate <= todayDateString &&
		(formData.admins || formData.users);

	const handleSaveClick = async () => {
		if (!isFormValid) {
			return;
		}
		const impactType = formData.admins && formData.users ? 3 : formData.admins ? 2 : 1;
		const entity = {
			...(editEntity ? { whatsNewId: editEntity.whatsNewId } : {}),
			versionId: formData.version || 0,
			title: formData.title,
			description: formData.description,
			whatsNewType: formData.typeId,
			whatsNewListType: 1,
			impactType,
			isAdminImpacted: formData.admins,
			isUserImpacted: formData.users,
			releaseDate: formData.releaseDate ? new Date(formData.releaseDate).toISOString() : null,
			isPublished: formData.status === 'published',
			documentRequests: screenshots,
		};
		try {
			setSaving(true);
			const response: any = editEntity ? await updateWhatsNewAPI(entity) : await createWhatsNewAPI(entity);
			if (response?.data?.isSuccess) {
				enqueueSnackbar(response.data.messageList?.Result, { variant: 'success' });
				setFormData(emptyFormState(whatsNewTypes[0]?.whatsNewTypeId, versions[0]?.oneGuruVersionId));
				setScreenshots([]);
				onSaved();
				handleClose();
			} else {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		} catch (error) {
			enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<MainDrawer
			drawerClassName='main-drawer-panel add-update-drawer'
			open={open}
			headerTitle={editEntity ? t('editUpdateTitle') : t('addUpdateTitle')}
			showHeaderCloseIcon
			handleDrawerClose={handleCancel}
			handleCancelClick={handleCancel}
			handleSaveClick={handleSaveClick}
			cancelBtnText={t('cancelBtn')}
			saveButtonText={t('saveEntryBtn')}
			isSaveButtonDisabled={!isFormValid || saving || uploadingScreenshot || deletingScreenshotIndex !== null}
			loader={saving}
		>
			<Box className='add-update-form'>
				<Box className='form-field'>
					<TextField
						fullWidth
						required
						id='update-title'
						name='title'
						label={t('titleLabel')}
						placeholder={t('titleLabel')}
						value={formData.title}
						onChange={handleTextChange}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>

				<Box className='form-field'>
					<TextField
						fullWidth
						required
						multiline
						minRows={4}
						id='update-description'
						name='description'
						label={t('descriptionLabel')}
						placeholder={t('descriptionPlaceholder')}
						value={formData.description}
						onChange={handleTextChange}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle2' className='field-label'>
						{t('screenshotsLabel')}
					</Typography>
					<Box
						className={`screenshot-dropzone ${isDragActive ? 'drag-active' : ''}`}
						onClick={() => screenshotInputRef.current?.click()}
						onDrop={handleScreenshotDrop}
						onDragOver={handleScreenshotDragOver}
						onDragLeave={handleScreenshotDragLeave}
					>
						<UploadIcon />
						<Typography className='dropzone-title'>{t('dragDropImages')}</Typography>
						<Typography className='dropzone-subtitle'>{t('multipleScreenshotsSupported')}</Typography>
					</Box>
					<input
						ref={screenshotInputRef}
						type='file'
						accept='image/png,image/jpeg,image/gif'
						multiple
						style={{ display: 'none' }}
						onChange={handleScreenshotInputChange}
					/>
					{screenshots.length > 0 && (
						<Box className='screenshot-list'>
							{screenshots.map((doc, index) => (
								<Box className='screenshot-thumb' key={`${doc.filePath}-${index}`}>
									<Tooltip arrow title={doc.uploadFileName || ''}>
										<img src={doc.filePath} alt={doc.uploadFileName || 'screenshot'} />
									</Tooltip>
									<OKRButton
										className='screenshot-thumb-remove'
										id={`remove-screenshot-${index}`}
										icon={<CrossIcon />}
										disabled={deletingScreenshotIndex === index}
										handleClick={() => removeScreenshot(index)}
									/>
								</Box>
							))}
						</Box>
					)}
				</Box>

				<Box className='form-field form-field-col2'>
					<TextField
						select
						required
						className='field-col2'
						id='update-version'
						name='version'
						label={t('versionLabel')}
						value={formData.version ?? ''}
						onChange={(event) => setFormData((prev) => ({ ...prev, version: Number(event.target.value) }))}
						InputLabelProps={{ shrink: true }}
					>
						{versions.map((versionOption) => (
							<MenuItem key={versionOption.oneGuruVersionId} value={versionOption.oneGuruVersionId}>
								{versionOption.version}
							</MenuItem>
						))}
					</TextField>
					<TextField
						required
						className='field-col2'
						id='update-release-date'
						name='releaseDate'
						type='date'
						label={t('releaseDateLabel')}
						value={formData.releaseDate}
						onChange={handleTextChange}
						InputLabelProps={{ shrink: true }}
						inputProps={{ max: todayDateString }}
					/>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle2' className='field-label'>
						{t('typeColumn')}
						<span className='asterisk'> *</span>
					</Typography>
					<Box className='type-toggle-group'>
						{whatsNewTypes.map((type) => (
							<OKRButton
								key={type.whatsNewTypeId}
								id={`type-${type.whatsNewTypeId}`}
								className={`type-toggle-btn type-toggle-${type.whatsNewTypeId} ${
									formData.typeId === type.whatsNewTypeId ? 'active' : ''
								}`}
								text={type.typeName}
								handleClick={() => setFormData((prev) => ({ ...prev, typeId: type.whatsNewTypeId }))}
							/>
						))}
					</Box>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle1' className='field-label'>
						{t('whosImpactedLabel')}
						<span className='asterisk'> *</span>
					</Typography>
					<Box className='impacted-audience-group'>
						<FormControlLabel
							className={`audience-chip ${formData.admins ? 'active' : ''}`}
							control={
								<Checkbox
									checked={formData.admins}
									icon={<BorderCheckboxIcon />}
									checkedIcon={<CheckedIcon />}
									onChange={(e) => setFormData((prev) => ({ ...prev, admins: e.target.checked }))}
								/>
							}
							label={t('adminsLabel')}
						/>
						<FormControlLabel
							className={`audience-chip ${formData.users ? 'active' : ''}`}
							control={
								<Checkbox
									checked={formData.users}
									icon={<BorderCheckboxIcon />}
									checkedIcon={<CheckedIcon />}
									onChange={(e) => setFormData((prev) => ({ ...prev, users: e.target.checked }))}
								/>
							}
							label={t('usersLabel')}
						/>
					</Box>
					<Typography className='field-helper-text'>{t('impactedAudienceHelperText')}</Typography>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle1' className='field-label'>
						{t('statusLabel')}
						<span className='asterisk'> *</span>
					</Typography>
					<Box className='status-toggle-group'>
						<OKRButton
							id='status-draft'
							className={`status-toggle-btn ${formData.status === 'draft' ? 'active' : ''}`}
							text={t('draftLabel')}
							handleClick={() => setFormData((prev) => ({ ...prev, status: 'draft' }))}
						/>
						<OKRButton
							id='status-published'
							className={`status-toggle-btn ${formData.status === 'published' ? 'active' : ''}`}
							text={t('publishedLabel')}
							handleClick={() => setFormData((prev) => ({ ...prev, status: 'published' }))}
						/>
					</Box>
				</Box>
			</Box>
		</MainDrawer>
	);
};
